import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, DollarSign, BarChart2, Lightbulb, MapPin, Clock, Check } from 'lucide-react';

/* ─────────────────────────────────────────────
   Feature 1 & 2: full-width alternating blocks
   (Labor Dashboard + Eva Insights — real screenshots)
   Features 3–5: compact 3-card grid below
───────────────────────────────────────────── */

const SHOWCASE_FEATURES = [
  {
    key: 'labor-dashboard',
    badge: 'Labor Dashboard',
    title: 'See every dollar before payroll closes.',
    desc: 'The Labor Dashboard turns approved timesheets into a real-time cost view — filtered by date range, location, and employee. No more end-of-month surprises.',
    bullets: [
      'Total labor cost and hours for any period',
      'Cost per employee bar chart — outliers highlighted',
      'Daily labor trend to spot expensive days early',
      'One-click CSV export for payroll review',
    ],
    img: '/help/labor-dashboard.jpg',
    alt: 'Labor cost dashboard showing KPI cards and employee cost breakdown — ReStaff',
    imgLeft: false,
    Icon: BarChart2,
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
  },
  {
    key: 'payroll',
    badge: 'Smart Payroll',
    title: 'Everything you need to run payroll.',
    desc: 'ReStaff auto-generates payslips the moment timesheets are approved. Review, finalize, and email every employee in minutes — no manual math, no spreadsheets.',
    bullets: [
      'Automatic payroll generation from approved timesheets',
      'PDF payslips emailed to employees on finalization',
      'CSV export + PDF summary for accounting and compliance',
      'Cash advance deductions tracked and applied automatically',
      'Overtime and holiday pay calculated correctly — always',
    ],
    img: '/help/payroll-runs-table.jpg',
    alt: 'Payroll runs table showing finalized payroll runs with email status icons — ReStaff',
    imgLeft: true,
    Icon: DollarSign,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  {
    key: 'eva-insights',
    badge: 'Eva Insights',
    title: 'Recommendations, not just reports.',
    desc: 'Eva scans your payroll data automatically and returns up to 3 actions — each with a dollar savings estimate. No chatbot, no API key, no setup. Just answers.',
    bullets: [
      '"John costs 57% more than average — Est. savings: $161"',
      '"Overstaffed on Mar 18 — reduce 1 staff to save $183"',
      'Sorts recommendations by highest dollar impact first',
      'Updates instantly when you change date or location filter',
    ],
    img: '/help/eva-insights.jpg',
    alt: 'Eva Insights showing actionable labor recommendations with dollar savings — ReStaff',
    imgLeft: false,
    Icon: Lightbulb,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
];

const COMPACT_FEATURES = [
  {
    key: 'shift-cost',
    Icon: DollarSign,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    title: 'Real-Time Shift Cost',
    desc: 'See the estimated cost of a shift as you edit it — before approving. Catch overtime premiums and unexpected costs before they hit payroll.',
    bullets: ['Cost shown per shift in the timesheet editor', 'Includes overtime and holiday multipliers', 'Immediate feedback on schedule decisions'],
  },
  {
    key: 'multi-location',
    Icon: MapPin,
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    title: 'Multi-Location Analysis',
    desc: 'Compare labor cost per hour across every branch. Eva automatically flags when one location costs 15%+ more than another.',
    bullets: ['Filter dashboard by individual location', 'Cross-location cost comparison', 'Location inefficiency alerts in Eva Insights'],
  },
  {
    key: 'payroll-accuracy',
    Icon: Clock,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Payroll Accuracy',
    desc: 'Overtime and holiday pay multipliers are calculated automatically — stacked correctly, every time. No manual overrides, no calculation errors.',
    bullets: ['Overtime threshold and multiplier configured per group', 'Holiday dates with custom pay rates', 'Payroll summary export ready for your provider'],
  },
];

function ShowcaseFeature({ feature, index }) {
  const { Icon, iconBg, iconColor, badge, title, desc, bullets, img, alt, imgLeft } = feature;
  return (
    <div
      className={`flex flex-col ${imgLeft ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-16 items-center`}
      data-testid={`core-feature-${feature.key}`}
    >
      {/* Screenshot */}
      <div className="w-full lg:w-[58%] flex-shrink-0">
        <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
          <img
            src={img}
            alt={alt}
            loading="lazy"
            width="960"
            height="540"
            className="w-full h-auto"
            style={{ maxWidth: '100%' }}
          />
        </div>
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <div className="inline-flex items-center gap-2 mb-5">
          <span className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon size={14} className={iconColor} />
          </span>
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{badge}</span>
        </div>

        <h3
          className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 leading-tight"
          style={{ fontFamily: 'Manrope' }}
        >
          {title}
        </h3>
        <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-7">{desc}</p>

        <ul className="space-y-3">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-3">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-orange-100 border border-orange-200 flex items-center justify-center flex-shrink-0">
                <Check size={10} className="text-orange-600" strokeWidth={3} />
              </span>
              <span className="text-slate-700 text-sm font-medium leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function CoreFeaturesSection() {
  return (
    <section
      className="py-16 md:py-28 bg-white px-4"
      id="core-features"
      aria-label="Core features"
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-widest mb-4">Workforce + Payroll, unified</p>
          <h2
            className="text-3xl md:text-5xl font-bold text-slate-900 leading-tight"
            style={{ fontFamily: 'Manrope' }}
          >
            From timesheets to payslips
            <br className="hidden sm:block" /> in one platform.
          </h2>
          <p className="mt-5 text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Real-time labor visibility. Automated payroll. AI-powered recommendations. All connected.
          </p>
        </div>

        {/* Showcase features (Labor Dashboard + Eva Insights) */}
        <div className="space-y-20 md:space-y-28">
          {SHOWCASE_FEATURES.map((f, i) => (
            <React.Fragment key={f.key}>
              <ShowcaseFeature feature={f} index={i} />
              {i < SHOWCASE_FEATURES.length - 1 && (
                <div className="border-t border-slate-100" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Divider */}
        <div className="text-center my-16 md:my-24">
          <div className="flex items-center justify-center gap-3">
            <div className="h-px bg-slate-200 w-12" />
            <span className="text-xs font-semibold tracking-widest text-orange-500 uppercase">
              Core Capabilities
            </span>
            <div className="h-px bg-slate-200 w-12" />
          </div>
        </div>

        {/* Compact feature cards (3 remaining) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {COMPACT_FEATURES.map(({ key, Icon, iconBg, iconColor, title, desc, bullets }) => (
            <div
              key={key}
              className="bg-white rounded-xl p-5 md:p-7 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200"
              data-testid={`compact-feature-${key}`}
            >
              <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center mb-3 flex-shrink-0`}>
                <Icon size={18} className={iconColor} />
              </div>
              <h3 className="text-slate-900 font-bold text-lg mb-2" style={{ fontFamily: 'Manrope' }}>
                {title}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-4">{desc}</p>
              <ul className="space-y-2">
                {bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <Check size={12} className="text-orange-500 flex-shrink-0" strokeWidth={3} />
                    <span className="text-slate-700 text-sm">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA row (hidden on mobile — sticky bottom CTA replaces it) */}
        <div className="mt-16 text-center hidden md:block">
          <Link to="/register">
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white text-base px-10 rounded-full shadow-md shadow-orange-200 hover:shadow-lg hover:shadow-orange-200 transition-all duration-200"
              data-testid="core-features-cta-btn"
            >
              Start Free Trial
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
          <p className="mt-3 text-sm text-slate-400">30-day free trial · Cancel anytime, no questions asked</p>
        </div>

      </div>
    </section>
  );
}
